import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import lang from "lang";
import { domainRegex } from "components/campaign/validationSchema";
import { selectCurrentSubscription } from "components/subscription/subscriptionSlice";
import { SUBSCRIPTION_STATUS } from "components/subscription/types";
import { getDomainSettings, saveDomainSettings } from "./service";
import { PreferredDomainRequestPayload } from "./types";
import { setDomainSettingsDetails } from "./domainSettingsSlice";
import { useUser } from "@clerk/clerk-react";

interface UseDomainSettingDialogProps {
  onClose: () => void;
}

export const useDomainSettingsDialog = ({
  onClose,
}: UseDomainSettingDialogProps) => {
  const { campaign: campaignCopy } = lang;
  const dispatch = useDispatch();
  const { user } = useUser();
  const subscriptionDetails = useSelector(selectCurrentSubscription);

  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const trialDomain = process.env.REACT_APP_CAMPAIGN_TRIAL_DOMAIN || "";
  const userNotifyTo = userData?.notifyTo
    ? userData?.notifyTo
    : userData?.email || "";
  const isTrial = subscriptionDetails.status === SUBSCRIPTION_STATUS.TRAILING;

  const [firstName, setFirstName] = useState(user?.firstName || "");
  const [lastName, setLastName] = useState(user?.lastName || "");
  const [nameError, setNameError] = useState<string | null>(null);

  const [domain, setDomain] = useState("");
  const [domainError, setDomainError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showSuccessEndTrialBanner, setShowSuccessEndTrialBanner] =
    useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isTrial && trialDomain) {
      setDomain(trialDomain);
      setDomainError(null);
    }
  }, [isTrial, trialDomain]);

  const validate = (value: string) => {
    if (!value || !domainRegex.test(value)) {
      setDomainError(campaignCopy.domainError);
      return false;
    }
    setDomainError(null);
    return true;
  };

  const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    setDomain(value);
    validate(value);
  };

  const handleWebsiteBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setDomain(e.target.value);
  };

  const handleWebsitePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    setDomain(pastedText);
    validate(pastedText);
  };

  const validateNames = () => {
    if (!firstName.trim() || !lastName.trim()) {
      setNameError("First name and last name are required.");
      return false;
    }
    setNameError(null);
    return true;
  };

  const handleSave = async () => {
    if (validateNames() && !validate(domain)) return;

    const payload: PreferredDomainRequestPayload = {
      preferredDomain: domain,
      mailboxFirstName: firstName,
      mailboxLastName: lastName,
    };

    setIsSaving(true);
    setApiError(null);

    try {
      await saveDomainSettings(payload);

      const domainSettingDetails = await getDomainSettings();
      if (domainSettingDetails && Array.isArray(domainSettingDetails)) {
        dispatch(setDomainSettingsDetails(domainSettingDetails));
      }

      setSuccessMessage(
        "Your domain settings have been added. It will take some time to reflect on your account."
      );

      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (error) {
      setApiError(
        "Something went wrong while saving your domain. Please try again."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubscriptionActivatedSuccesfully = () => {
    setShowSuccessEndTrialBanner(true);
    setDomain("");
    setTimeout(() => setShowSuccessEndTrialBanner(false), 5000);
  };

  return {
    firstName,
    lastName,
    nameError,
    domain,
    domainError,
    apiError,
    successMessage,
    isTrial,
    isSaving,
    userNotifyTo,
    userData,
    subscriptionDetails,
    showSuccessEndTrialBanner,
    setShowSuccessEndTrialBanner,
    handleDomainChange,
    handleWebsiteBlur,
    handleWebsitePaste,
    handleSave,
    validateNames,
    setFirstName,
    setLastName,
    handleSubscriptionActivatedSuccesfully,
  };
};
